/**
 * useActivityLogger
 *
 * Amacı:
 * - Bir medya içeriği (video vb.) oynatılırken, rastgele fakat kurallı zamanlarda etkileşim popup'ları göstermek
 *   ve tüm bu etkileşimleri loglamak.
 * - Popup hedef zamanları `generatePopupTimePoints(duration)` ile belirlenir; başlangıç/bitişe yakın olmama,
 *   minimum mesafe ve küçük rastgele sapmalar (jitter) gibi kısıtlar uygular.
 *
 * Parametreler:
 * - assetId: string | number
 *   Log kayıtlarında içerik kimliği olarak kullanılır. Değiştiğinde state sıfırlanır ve `assetSelected` loglanır.
 * - currentTime: number (saniye)
 *   Oynatımın mevcut konumu. Hedef zamanlara gelindiğinde popup tetiklenir.
 * - isPlaying: boolean
 *   Oynatım durduğunda veya popup açıkken tetikleme yapılmaz.
 * - duration: number (saniye)
 *   Video süresi. Süre geçerliyse hedef zamanlar bir kez üretilir ve `popupTargetsAssigned` loglanır.
 * - onPopupShow?: () => void
 *   Popup açıldığında çağrılır (genelde oynatmayı durdurmak için kullanılır).
 * - onContinue?: (clickedAfterSeconds: number | null) => void
 *   Kullanıcı devam ettiğinde çağrılır; popup açılmasından tıklamaya kadar geçen süreyi saniye cinsinden alır.
 *
 * Dönüş Değeri:
 * - popupVisible: boolean — Popup görünür mü?
 * - handleContinue: () => void — Kullanıcı “Devam” tıkladığında çağrılacak handler; `popupContinue` loglar ve
 *   `onContinue` callback'ini süre bilgisiyle tetikler.
 *
 * Loglanan Etkinlikler (eventType):
 * - 'assetSelected' — Yeni bir assetId seçildiğinde
 * - 'popupTargetsAssigned' — Hedef zamanlar üretildiğinde; { duration, timePoints }
 * - 'popupShown' — Popup gösterildiğinde; { triggerTime }
 * - 'popupContinue' — Kullanıcı devam ettiğinde; { clickedAfter }
 *
 * Davranış ve Kenar Durumları:
 * - Aynı asset için hedef zamanlar yalnızca bir kez atanır (loggedAssetId kontrolü).
 * - assetId değiştiğinde popup geçmişi ve sayaçlar sıfırlanır; açık popup kapatılır.
 * - isPlaying=false iken veya popupVisible=true iken tetikleme yapılmaz.
 * - duration <= 60 ise hedef zamanı olmayabilir (kurala bağlı olarak 0 hedef dönebilir).
 * - currentTime değeri geçersizse (undefined/null) tetikleme yapılmaz.
 * - Her hedef zaman yalnızca bir kez tetiklenir (shownPopupsRef Set ile izlenir).
 *
 * Kısa Kullanım Örneği:
 *
 * const { popupVisible, handleContinue } = useActivityLogger({
 *   assetId,
 *   currentTime,   // player.currentTime (saniye)
 *   isPlaying,     // player.isPlaying
 *   duration,      // player.duration
 *   onPopupShow: () => player.pause(),
 *   onContinue: (after) => player.play()
 * });
 *
 * {popupVisible && <MyPopup onContinue={handleContinue} />}
 */

import {useCallback, useEffect, useRef, useState} from 'react';
import {generatePopupTimePoints} from "./useRandomTimePoints";
import LogService from "../../../service/log/LogService";

const service = new LogService('logActivity');

export function useActivityLogger({
                                   assetId,
                                   currentTime,
                                   isPlaying,
                                   duration,
                                   onPopupShow,
                                   onContinue,
                               }) {
    const [popupVisible, setPopupVisible] = useState(false);
    const [timePoints, setTimePoints] = useState([]);

    const shownPopupsRef = useRef(new Set());
    const popupStartRef = useRef(null);

    const [loggedAssetId, setLoggedAssetId] = useState(null);


    const log = useCallback((eventType, metadata) => {
        service.create(
            {
                eventType,
                assetId,
                metadata: JSON.stringify(metadata)
            },
        );

        // Etkinliği logEventBus ile yayınla
        // logEventBus.emit({ type: eventType, timestamp: Date.now() });
    }, [assetId]);

    useEffect(() => {
        if (assetId && assetId !== loggedAssetId && typeof duration === 'number' && duration > 0) {
            const points = generatePopupTimePoints(duration);
            setTimePoints(points);
            log('popupTargetsAssigned', { duration, timePoints: points });
            setLoggedAssetId(assetId); // ✅ logladık, tekrar loglamayacağız
        }
    }, [assetId, duration, loggedAssetId, log]);

// assetId değişince geçmiş popup'ları sıfırla
    useEffect(() => {
        shownPopupsRef.current = new Set();
        popupStartRef.current = null;
        setPopupVisible(false);
        if (assetId) {
            log('assetSelected');
        }
    }, [assetId, log]);


    useEffect(() => {
        if (!isPlaying || popupVisible || !currentTime || timePoints.length === 0) return;

        const popupTime = timePoints.find(t => !shownPopupsRef.current.has(t) && currentTime >= t && currentTime < t + 1);

        if (popupTime !== undefined) {
            popupStartRef.current = Date.now();
            shownPopupsRef.current.add(popupTime);
            setPopupVisible(true);
            log('popupShown', { triggerTime: popupTime });
            onPopupShow?.(); // 📌 Burada durdurma işlemi yapılır
        }

    }, [assetId, currentTime, isPlaying, popupVisible, timePoints, onPopupShow, log]);

    const handleContinue = () => {
        setPopupVisible(false);
        const clickedAfter = popupStartRef.current ? (Date.now() - popupStartRef.current) / 1000 : null;
        log('popupContinue', { clickedAfter });
        onContinue?.(clickedAfter);
    };

    return {popupVisible, handleContinue};
}
